/**
 * Selecting several projects at once, to archive or restore them together.
 *
 * The list is the visible rows, in order, so every function takes that order:
 * a range means "the rows between", and "select all" means "all the rows you
 * can see", never ones a filter has hidden.
 */

/** The ids a person may pick: visible rows they are allowed to archive or restore. */
export function selectableIds<T extends { projectId: string }>(
  shown: readonly T[], canAct: (projectId: string) => boolean,
): string[] {
  return shown.filter((p) => canAct(p.projectId)).map((p) => p.projectId);
}

/** What is still selected once rows have been filtered away or lost permission. */
export function pruneSelection(selected: ReadonlySet<string>, selectable: readonly string[]): Set<string> {
  const ok = new Set(selectable);
  return new Set([...selected].filter((id) => ok.has(id)));
}

/**
 * Tick or untick one row. With shift held and an earlier click to anchor on,
 * the whole run between the two rows follows the clicked row's new state.
 */
export function toggleRow(
  selectable: readonly string[], selected: ReadonlySet<string>,
  id: string, checked: boolean, anchor: string | null, shift: boolean,
): Set<string> {
  const next = new Set(selected);
  const to = selectable.indexOf(id);
  const from = anchor === null ? -1 : selectable.indexOf(anchor);
  const ids = shift && from >= 0 && to >= 0
    ? selectable.slice(Math.min(from, to), Math.max(from, to) + 1)
    : [id];
  for (const i of ids) checked ? next.add(i) : next.delete(i);
  return next;
}

/** The header box: everything selectable, or nothing if it is all selected already. */
export function toggleAll(selectable: readonly string[], selected: ReadonlySet<string>): Set<string> {
  const all = selectable.length > 0 && selectable.every((id) => selected.has(id));
  return all ? new Set() : new Set(selectable);
}

/** Run one request per id, in turn, and report what did not go through. */
export async function runEach(
  ids: readonly string[], fn: (id: string) => Promise<unknown>,
): Promise<{ done: string[]; failed: { id: string; message: string }[] }> {
  const done: string[] = [];
  const failed: { id: string; message: string }[] = [];
  for (const id of ids) {
    try { await fn(id); done.push(id); }
    catch (e) { failed.push({ id, message: (e as Error).message }); }
  }
  return { done, failed };
}
